/**
 * Local drafts (SPEC §12): IndexedDB, namespaced by user + household + form, 7-day max age, wiped on
 * logout / identity change. Only plain field values are stored - never files, photos or document contents.
 * A draft is NEVER a confirmation that anything was sent.
 */

export const DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000
const MAX_DRAFT_BYTES = 200_000

export interface DraftNamespace {
  userId: string
  householdId: string
  formId: string
}

export interface StoredDraft {
  key: string
  userId: string
  householdId: string
  formId: string
  /** JSON-serialised field values (plain data only). */
  data: string
  savedAt: number
}

export type DraftSaveResult = { ok: true; savedAt: number } | { ok: false; reason: 'quota' | 'unavailable' | 'too_large' }
export interface LoadedDraft<T> {
  data: T
  savedAt: number
}

/** Storage seam: the real one is IndexedDB, tests use memory or a failing backend. */
export interface DraftBackend {
  get(key: string): Promise<StoredDraft | undefined>
  put(draft: StoredDraft): Promise<void>
  delete(key: string): Promise<void>
  all(): Promise<StoredDraft[]>
  clear(): Promise<void>
}

export const draftKey = (ns: DraftNamespace) =>
  [ns.userId, ns.householdId, ns.formId].map(encodeURIComponent).join('|')

function classify(error: unknown): 'quota' | 'unavailable' {
  const name = (error as { name?: string } | null)?.name
  return name === 'QuotaExceededError' || name === 'NS_ERROR_DOM_QUOTA_REACHED' ? 'quota' : 'unavailable'
}

export function createDraftService(backend: () => DraftBackend, now: () => number = () => Date.now()) {
  // After logout the unmounting form must not write its draft back; lifted when a user is known again.
  let writesBlocked = false
  return {
    blockWrites(blocked: boolean) {
      writesBlocked = blocked
    },
    async save(ns: DraftNamespace, values: unknown): Promise<DraftSaveResult> {
      if (writesBlocked) return { ok: false, reason: 'unavailable' }
      let data: string
      try {
        data = JSON.stringify(values)
      } catch {
        return { ok: false, reason: 'unavailable' }
      }
      if (data.length > MAX_DRAFT_BYTES) return { ok: false, reason: 'too_large' }
      const savedAt = now()
      try {
        await backend().put({ key: draftKey(ns), ...ns, data, savedAt })
        return { ok: true, savedAt }
      } catch (error) {
        return { ok: false, reason: classify(error) }
      }
    },

    /** Returns null when missing, expired (and deletes it), corrupt or storage is unavailable. */
    async load<T>(ns: DraftNamespace): Promise<LoadedDraft<T> | null> {
      try {
        const key = draftKey(ns)
        const stored = await backend().get(key)
        if (!stored) return null
        if (now() - stored.savedAt > DRAFT_MAX_AGE_MS) {
          await backend().delete(key)
          return null
        }
        return { data: JSON.parse(stored.data) as T, savedAt: stored.savedAt }
      } catch {
        return null
      }
    },

    async remove(ns: DraftNamespace): Promise<void> {
      try {
        await backend().delete(draftKey(ns))
      } catch {
        /* unavailable storage: nothing to remove */
      }
    },

    /** Startup purge of drafts older than 7 days. Returns how many were removed. */
    async purgeExpired(): Promise<number> {
      try {
        const expired = (await backend().all()).filter((d) => now() - d.savedAt > DRAFT_MAX_AGE_MS)
        await Promise.all(expired.map((d) => backend().delete(d.key)))
        return expired.length
      } catch {
        return 0
      }
    },

    /** Logout / identity change. Best effort: never throws. */
    async clearAll(): Promise<void> {
      try {
        await backend().clear()
      } catch {
        /* ignore */
      }
    },
  }
}

/* ---------- IndexedDB backend (no dependency: a few small promisified calls) ---------- */

const DB_NAME = 'lumora-drafts'
const STORE = 'drafts'

function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error ?? new Error('indexeddb'))
  })
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') return reject(new Error('indexeddb unavailable'))
    let opening: IDBOpenDBRequest
    try {
      opening = indexedDB.open(DB_NAME, 1)
    } catch (error) {
      return reject(error instanceof Error ? error : new Error('indexeddb'))
    }
    opening.onupgradeneeded = () => {
      if (!opening.result.objectStoreNames.contains(STORE)) opening.result.createObjectStore(STORE, { keyPath: 'key' })
    }
    opening.onsuccess = () => {
      const db = opening.result
      db.onversionchange = () => db.close()
      resolve(db)
    }
    opening.onerror = () => reject(opening.error ?? new Error('indexeddb'))
    opening.onblocked = () => reject(new Error('indexeddb blocked'))
  })
}

export function createIndexedDbBackend(): DraftBackend {
  let dbPromise: Promise<IDBDatabase> | null = null
  const db = () => {
    dbPromise ??= openDb().catch((error) => {
      dbPromise = null // retry next time (e.g. storage became available)
      throw error
    })
    return dbPromise
  }
  const tx = async <T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> => {
    const database = await db()
    const transaction = database.transaction(STORE, mode)
    const done = new Promise<void>((resolve, reject) => {
      transaction.oncomplete = () => resolve()
      transaction.onerror = () => reject(transaction.error ?? new Error('indexeddb'))
      transaction.onabort = () => reject(transaction.error ?? new Error('indexeddb aborted'))
    })
    const result = await request(run(transaction.objectStore(STORE)))
    await done
    return result
  }
  return {
    get: (key) => tx('readonly', (s) => s.get(key) as IDBRequest<StoredDraft | undefined>),
    put: async (draft) => void (await tx('readwrite', (s) => s.put(draft))),
    delete: async (key) => void (await tx('readwrite', (s) => s.delete(key))),
    all: () => tx('readonly', (s) => s.getAll() as IDBRequest<StoredDraft[]>),
    clear: async () => void (await tx('readwrite', (s) => s.clear())),
  }
}

/** The app-wide draft service (IndexedDB). */
const indexedDbBackend = createIndexedDbBackend()
export const drafts = createDraftService(() => indexedDbBackend)
